import React, { useState, useEffect, useCallback } from 'react';
import TimeTrackingNav from '../components/TimeTrackingNav';
import timeTrackingService from '../services/timeTrackingService';
import { usePermissions } from '../context/PermissionContext';

const fmt = d => d.toISOString().split('T')[0];

function getPeriodDates(payPeriodType, offset = 0) {
  const now = new Date();

  if (payPeriodType === 'monthly') {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: fmt(start), to: fmt(end) };
  }

  if (payPeriodType === 'weekly') {
    // current week = offset 0 (Mon–Sun), offset -1 = previous week, etc.
    const day = now.getDay() === 0 ? 6 : now.getDay() - 1; // days since Monday
    const monday = new Date(now); monday.setDate(now.getDate() - day + offset * 7);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return { from: fmt(monday), to: fmt(sunday) };
  }

  if (payPeriodType === 'bi_weekly') {
    // Anchor: Jan 1 of the epoch year as start of first bi-weekly period
    const anchor = new Date(now.getFullYear(), 0, 1);
    const daysSince = Math.floor((now - anchor) / 86400000);
    const periodIndex = Math.floor(daysSince / 14) + offset;
    const start = new Date(anchor); start.setDate(anchor.getDate() + periodIndex * 14);
    const end   = new Date(start);  end.setDate(start.getDate() + 13);
    return { from: fmt(start), to: fmt(end) };
  }

  // Default: semi_monthly (1–15, 16–end)
  const half = now.getDate() <= 15 ? 0 : 1;
  const totalHalves = now.getFullYear() * 24 + now.getMonth() * 2 + half + offset;
  const yr  = Math.floor(totalHalves / 24);
  const mo  = Math.floor((totalHalves % 24) / 2);
  const isFirst = totalHalves % 2 === 0;
  const start = new Date(yr, mo, isFirst ? 1 : 16);
  const endDay = isFirst ? 15 : new Date(yr, mo + 1, 0).getDate();
  const end   = new Date(yr, mo, endDay);
  return { from: fmt(start), to: fmt(end) };
}

function getPeriodLabel(from, to) {
  const f = new Date(from + 'T12:00:00');
  const t = new Date(to   + 'T12:00:00');
  const mo = f.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  const fDay = f.getDate();
  const tDay = t.getDate();
  const fMo = f.getMonth();
  const tMo = t.getMonth();
  if (fMo !== tMo) {
    return `${f.toLocaleString('en-US', { month: 'short', day: 'numeric' })} – ${t.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return `${mo} (${fDay}–${tDay})`;
}

function fmtHrsMin(minutes) {
  if (!minutes) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}h ${m}m`;
}

export default function TimeTrackingReportsPage() {
  const { companySettings } = usePermissions();
  const payPeriodType = companySettings?.payPeriodType || 'semi_monthly';

  const [summary, setSummary]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [eventsByEmp, setEventsByEmp]   = useState({}); // empId → [] | 'loading'

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setEventsByEmp(prev => {
      if (prev[id] !== undefined) return prev;
      const next = { ...prev, [id]: 'loading' };
      timeTrackingService.getEmployeeEvents(id)
        .then(evs => setEventsByEmp(m => ({ ...m, [id]: evs || [] })))
        .catch(() => setEventsByEmp(m => ({ ...m, [id]: [] })));
      return next;
    });
  };

  const today = new Date().toISOString().split('T')[0];
  const [rangeMode, setRangeMode] = useState('current');  // current | prev | custom
  const [from, setFrom] = useState(() => getPeriodDates('semi_monthly', 0).from);
  const [to, setTo]     = useState(() => getPeriodDates('semi_monthly', 0).to);

  useEffect(() => {
    if (rangeMode === 'current') { const p = getPeriodDates(payPeriodType, 0);  setFrom(p.from); setTo(p.to); }
    if (rangeMode === 'prev')    { const p = getPeriodDates(payPeriodType, -1); setFrom(p.from); setTo(p.to); }
  }, [rangeMode, payPeriodType]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await timeTrackingService.getSummary({ from, to });
      setSummary(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totalHours = summary.reduce((s, r) => s + r.totalNetHours, 0);
  const totalCost  = summary.reduce((s, r) => s + r.estimatedCost, 0);
  const totalPto   = summary.reduce((s, r) => s + (r.ptoHours || 0), 0);
  const totalSick  = summary.reduce((s, r) => s + (r.sickHours || 0), 0);
  const headcount  = summary.length;

  async function doExportTimecard(emp) {
    setExporting(`tc-${emp.employee.id}`);
    try {
      await timeTrackingService.exportTimecard({ employeeId: emp.employee.id, from, to });
    } catch { /* ignore */ }
    finally { setExporting(''); }
  }

  async function doExportPayPeriod() {
    setExporting('pp');
    try {
      await timeTrackingService.exportPayPeriod({ from, to });
    } catch { /* ignore */ }
    finally { setExporting(''); }
  }

  return (
    <div className="container mt-4">
      <TimeTrackingNav />
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <h5 className="mb-0">Reports</h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={doExportPayPeriod} disabled={exporting === 'pp'}>
          {exporting === 'pp' ? <span className="spinner-border spinner-border-sm me-1" /> : '📥 '}
          Export Pay Period CSV
        </button>
      </div>

      {/* Period Selector */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <div className="btn-group btn-group-sm">
                {[['current','Current Period'],['prev','Previous Period'],['custom','Custom']].map(([v,l]) => (
                  <button key={v} className={`btn btn-outline-secondary ${rangeMode === v ? 'active' : ''}`} onClick={() => setRangeMode(v)}>{l}</button>
                ))}
              </div>
            </div>
            {rangeMode === 'custom' ? (
              <>
                <div className="col-auto">
                  <label className="form-label mb-0 small">From</label>
                  <input className="form-control form-control-sm" type="date" value={from} onChange={e => setFrom(e.target.value)} />
                </div>
                <div className="col-auto">
                  <label className="form-label mb-0 small">To</label>
                  <input className="form-control form-control-sm" type="date" max={today} value={to} onChange={e => setTo(e.target.value)} />
                </div>
                <div className="col-auto">
                  <button className="btn btn-sm btn-secondary" onClick={load}>Apply</button>
                </div>
              </>
            ) : (
              <div className="col-auto text-muted small">{getPeriodLabel(from, to)}</div>
            )}
          </div>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="row g-3 mb-3">
        {[
          { label: 'Total Net Hours', value: totalHours.toFixed(1), icon: '⏱' },
          { label: 'Employees', value: headcount, icon: '👥' },
          { label: 'Est. Labor Cost', value: `$${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: '💵' },
        ].map(c => (
          <div key={c.label} className="col-md-4">
            <div className="card text-center">
              <div className="card-body py-3">
                <div style={{ fontSize: '1.6rem' }}>{c.icon}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{c.value}</div>
                <div className="text-muted small">{c.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {(totalPto > 0 || totalSick > 0) && (
        <div className="text-muted small mb-3" style={{ marginTop: -8 }}>
          Total Net Hours includes {[
            totalPto > 0 && `${totalPto.toFixed(1)}h PTO`,
            totalSick > 0 && `${totalSick.toFixed(1)}h Sick`,
          ].filter(Boolean).join(' + ')} (paid same as worked hours).
        </div>
      )}

      {/* Summary table */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" /></div>
      ) : summary.length === 0 ? (
        <div className="text-muted text-center py-5">No time data for this period.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 24 }} />
                <th>Employee</th>
                <th>Pay Type</th>
                <th>Rate</th>
                <th>Shifts</th>
                <th>Net Hours</th>
                <th>Est. Cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row, i) => {
                const emp = row.employee;
                const isHourly = emp.payType === 'hourly';
                const isExpanded = expandedRows.has(emp.id);
                const empEvents = eventsByEmp[emp.id];
                const periodEvents = Array.isArray(empEvents)
                  ? empEvents.filter(ev =>
                      ev.days.some(d => d.date >= from && d.date <= to)
                    ).map(ev => ({
                      ...ev,
                      periodDays: ev.days.filter(d => d.date >= from && d.date <= to),
                    }))
                  : [];
                const periodEventMinutes = periodEvents.reduce((s, ev) =>
                  s + ev.periodDays.reduce((s2, d) => s2 + (d.actualMinutes ?? d.shiftMinutes ?? 0), 0), 0);

                return (
                  <React.Fragment key={i}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleRow(emp.id)}
                    >
                      <td style={{ color: '#6c757d', fontSize: 11 }}>{isExpanded ? '▼' : '▶'}</td>
                      <td className="fw-semibold">
                        {emp.firstName} {emp.lastName}
                        {Array.isArray(empEvents) && periodEventMinutes > 0 && (
                          <span className="badge bg-primary ms-2" style={{ fontSize: 10 }}>
                            +{fmtHrsMin(periodEventMinutes)} events
                          </span>
                        )}
                      </td>
                      <td className="text-capitalize">{emp.payType}</td>
                      <td>
                        {isHourly
                          ? emp.hourlyRate ? `$${Number(emp.hourlyRate).toFixed(2)}/hr` : '—'
                          : emp.annualSalary ? `$${Math.round(Number(emp.annualSalary) / 12).toLocaleString()}/mo` : '—'}
                      </td>
                      <td>{row.shiftCount}</td>
                      <td>
                        {row.totalNetHours.toFixed(2)}
                        {(row.ptoHours > 0 || row.sickHours > 0) && (
                          <div className="text-muted" style={{ fontSize: 10, fontWeight: 400 }}>
                            incl. {[
                              row.ptoHours > 0 && `${row.ptoHours.toFixed(2)}h PTO`,
                              row.sickHours > 0 && `${row.sickHours.toFixed(2)}h Sick`,
                            ].filter(Boolean).join(' + ')}
                          </div>
                        )}
                      </td>
                      <td>${row.estimatedCost.toFixed(2)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => doExportTimecard(row)}
                          disabled={!!exporting}
                          title="Export detailed timecard CSV"
                        >
                          {exporting === `tc-${emp.id}` ? <span className="spinner-border spinner-border-sm" /> : '📥'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td />
                        <td colSpan={7} style={{ background: '#f8f9fa', padding: '8px 12px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#495057', marginBottom: 6 }}>
                            Event hours in this period
                          </div>
                          {empEvents === 'loading' ? (
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              <span className="spinner-border spinner-border-sm me-1" />Loading…
                            </div>
                          ) : periodEvents.length === 0 ? (
                            <div className="text-muted" style={{ fontSize: 12 }}>No event activity in this period.</div>
                          ) : (
                            <table className="table table-sm mb-0" style={{ fontSize: 12, background: 'transparent' }}>
                              <thead>
                                <tr className="text-muted" style={{ fontSize: 11 }}>
                                  <th style={{ fontWeight: 500 }}>Event</th>
                                  <th style={{ fontWeight: 500 }}>Date</th>
                                  <th style={{ fontWeight: 500 }}>Time</th>
                                  <th style={{ fontWeight: 500 }} className="text-end">Hours</th>
                                  <th style={{ fontWeight: 500 }} className="text-center">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {periodEvents.flatMap(ev =>
                                  ev.periodDays.map((d, di) => {
                                    const hasActual = d.actualMinutes != null;
                                    const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                    return (
                                      <tr key={`${ev.event.id}-${di}`}>
                                        <td>
                                          <span className="badge bg-primary me-1" style={{ fontSize: 9 }}>EVENT</span>
                                          {ev.event.name}
                                          {ev.isInventoryManager && (
                                            <span className="badge bg-info text-dark ms-1" style={{ fontSize: 9 }}>Inv. Mgr</span>
                                          )}
                                        </td>
                                        <td className="text-muted">{d.date}</td>
                                        <td className="text-muted">
                                          {hasActual && d.clockIn && d.clockOut
                                            ? `${fmtTime(d.clockIn)}–${fmtTime(d.clockOut)}`
                                            : d.shiftStart && d.shiftEnd
                                              ? `${d.shiftStart.slice(0,5)}–${d.shiftEnd.slice(0,5)}`
                                              : '—'}
                                        </td>
                                        <td className="text-end fw-semibold">
                                          {hasActual
                                            ? fmtHrsMin(d.actualMinutes)
                                            : d.shiftMinutes
                                              ? fmtHrsMin(d.shiftMinutes)
                                              : '—'}
                                        </td>
                                        <td className="text-center">
                                          {hasActual
                                            ? <span className="badge bg-success" style={{ fontSize: 9 }}>Clocked</span>
                                            : d.confirmedByEmployee
                                              ? <span className="badge bg-success" style={{ fontSize: 9 }}>Confirmed</span>
                                              : d.published
                                                ? <span className="badge bg-primary" style={{ fontSize: 9 }}>Scheduled</span>
                                                : <span className="badge bg-secondary" style={{ fontSize: 9 }}>Draft</span>}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                              {periodEventMinutes > 0 && (
                                <tfoot className="table-light fw-semibold" style={{ fontSize: 12 }}>
                                  <tr>
                                    <td colSpan={3} className="text-end text-muted" style={{ fontWeight: 500 }}>
                                      Total event hours in period
                                    </td>
                                    <td className="text-end">{fmtHrsMin(periodEventMinutes)}</td>
                                    <td />
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          )}
                          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                            Net Hours ({row.totalNetHours.toFixed(2)}h) = clocked hours ({row.workedHours.toFixed(2)}h)
                            {(row.ptoHours > 0 || row.sickHours > 0) && (
                              <> + paid time off ({[
                                row.ptoHours > 0 && `${row.ptoHours.toFixed(2)}h PTO`,
                                row.sickHours > 0 && `${row.sickHours.toFixed(2)}h Sick`,
                              ].filter(Boolean).join(' + ')})</>
                            )}
                            . Event hours above reflect actual clock-in/out when available, otherwise scheduled shift times.
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="table-light fw-semibold">
              <tr>
                <td />
                <td colSpan={3}>Total</td>
                <td>{totalHours.toFixed(2)}</td>
                <td>${totalCost.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="alert alert-info small mt-3 mb-0">
        <strong>Note:</strong> Estimated cost uses effective hourly rate (annual salary ÷ 52 weeks ÷ 40 hrs). Actual payroll disbursement is processed externally and may differ.
      </div>
    </div>
  );
}
